import { Grid, makeStyles } from "@material-ui/core";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import FavoriteBorderIcon from "@material-ui/icons/FavoriteBorder";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import ThumbUpAltIcon from "@material-ui/icons/ThumbUpAlt";
import YouTubeIcon from "@material-ui/icons/YouTube";
import { motion, useTransform, useViewportScroll } from "framer-motion";
import React, { MutableRefObject, useEffect, useRef, useState } from "react";
import { useIsMobile } from "src/hooks/isMobile";
import { useWindowSize } from "../../../../hooks/windowSize";

const useStyles = makeStyles((theme) => ({
  mainWrapper: {
    backgroundColor: "white",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    backgroundSize: "cover",
    height: "75vh",
    position: "relative",
    backgroundImage: "url(/assets/bas/hand-01.png)",
    [theme.breakpoints.down("md")]: {
      height: "75vh",
    },
  },
}));

export const PhoneAnimation: React.FC = () => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const size = useWindowSize();
  const youtubeRef = useRef(null);
  const mailRef = useRef(null);
  const favoriteRef = useRef(null);
  const chatBubbleRef = useRef(null);
  const thumbUp1Ref = useRef(null);
  const thumbUp2Ref = useRef(null);
  const thumbUp3Ref = useRef(null);
  const { scrollY } = useViewportScroll();
  const [youtubePosition, setYoutubePositionPosition] = useState<number>(0);
  const [mailPosition, setMailPositionPosition] = useState<number>(0);
  const [favoritePosition, setFavoritePositionPosition] = useState<number>(0);
  const [chatBubblePosition, setChatBubblePositionPosition] =
    useState<number>(0);
  const [thumbUp1Position, setThumbUp1PositionPosition] = useState<number>(0);
  const [thumbUp2Position, setThumbUp2PositionPosition] = useState<number>(0);
  const [thumbUp3Position, setThumbUp3PositionPosition] = useState<number>(0);

  const addOffsetTopFromParent = (ref: MutableRefObject<any>) => {
    const currentRefOffsetTop = ref.current.offsetTop;
    const offsetParent = ref.current.offsetParent;
    return currentRefOffsetTop + offsetParent?.offsetTop;
  };

  useEffect(() => {
    if (
      youtubeRef.current &&
      mailRef.current &&
      favoriteRef.current &&
      chatBubbleRef.current &&
      thumbUp1Ref.current &&
      thumbUp2Ref.current &&
      thumbUp3Ref.current
    ) {
      setYoutubePositionPosition(addOffsetTopFromParent(youtubeRef));
      setMailPositionPosition(addOffsetTopFromParent(mailRef));
      setFavoritePositionPosition(addOffsetTopFromParent(favoriteRef));
      setChatBubblePositionPosition(addOffsetTopFromParent(chatBubbleRef));
      setThumbUp1PositionPosition(addOffsetTopFromParent(thumbUp1Ref));
      setThumbUp2PositionPosition(addOffsetTopFromParent(thumbUp2Ref));
      setThumbUp3PositionPosition(addOffsetTopFromParent(thumbUp3Ref));
    }
  }, [
    youtubeRef.current,
    mailRef.current,
    favoriteRef.current,
    chatBubbleRef.current,
    thumbUp1Ref.current,
    thumbUp2Ref.current,
    thumbUp3Ref.current,
  ]);

  const youTube = useTransform(
    scrollY,
    [
      youtubePosition - size.innerHeight * 0.8,
      youtubePosition - size.innerHeight * 0.5,
    ],
    [0, isMobile ? 0.7 : 1]
  );

  const mail = useTransform(
    scrollY,
    [
      mailPosition - size.innerHeight * 0.8,
      mailPosition - size.innerHeight * 0.5,
    ],
    [0, isMobile ? 0.8 : 1]
  );

  const favorite = useTransform(
    scrollY,
    [
      favoritePosition - size.innerHeight * 0.8,
      favoritePosition - size.innerHeight * 0.5,
    ],
    [0, isMobile ? 0.7 : 1]
  );

  const chatBubble = useTransform(
    scrollY,
    [
      chatBubblePosition - size.innerHeight * 0.7,
      chatBubblePosition - size.innerHeight * 0.5,
    ],
    [0, isMobile ? 0.7 : 1]
  );

  const thumbUp1 = useTransform(
    scrollY,
    [
      thumbUp1Position - size.innerHeight * 0.8,
      thumbUp1Position - size.innerHeight * 0.5,
    ],
    [0, isMobile ? 0.7 : 1]
  );

  const thumbUp2 = useTransform(
    scrollY,
    [
      thumbUp2Position - size.innerHeight * 0.8,
      thumbUp2Position - size.innerHeight * 0.5,
    ],
    [0, isMobile ? 1 : 1]
  );

  const thumbUp3 = useTransform(
    scrollY,
    [
      thumbUp3Position - size.innerHeight * 0.7,
      thumbUp3Position - size.innerHeight * 0.5,
    ],
    [0, isMobile ? 0.7 : 1]
  );

  return (
    <Grid item className={classes.mainWrapper} xs={12} md={6}>
      <motion.div
        ref={youtubeRef}
        style={{
          scale: youTube,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "80px",
          width: "80px",
          backgroundColor: "#FF0000",
          left: isMobile ? "1%" : "23%",
          top: isMobile ? "20%" : "23%",
          position: "absolute",
          borderRadius: "50%",
        }}
      >
        <YouTubeIcon fontSize="large" />
      </motion.div>

      <motion.div
        ref={mailRef}
        style={{
          scale: mail,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "80px",
          width: "80px",
          backgroundColor: "#78C257",
          right: isMobile ? "14%" : "32%",
          top: isMobile ? "2%" : "5%",
          position: "absolute",
          borderRadius: "50%",
        }}
      >
        <MailOutlineIcon fontSize="large" />
      </motion.div>

      <motion.div
        ref={favoriteRef}
        style={{
          scale: favorite,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "80px",
          width: "80px",
          backgroundColor: "#833AB4",
          left: isMobile ? "17%" : "35%",
          top: isMobile ? "5%" : "10%",
          position: "absolute",
          borderRadius: "50%",
        }}
      >
        <FavoriteBorderIcon fontSize="large" />
      </motion.div>

      <motion.div
        ref={thumbUp1Ref}
        style={{
          scale: thumbUp1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "80px",
          width: "80px",
          backgroundColor: "#4267B2",
          right: isMobile ? "16%" : "37%",
          bottom: isMobile ? "25%" : "55%",
          position: "absolute",
          borderRadius: "50%",
        }}
      >
        <ThumbUpAltIcon fontSize="large" />
      </motion.div>

      <motion.div
        ref={chatBubbleRef}
        style={{
          scale: chatBubble,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100px",
          width: "100px",
          backgroundColor: "#FFFC00",
          right: isMobile ? "12%" : "25%",
          bottom: isMobile ? "8%" : "15%",
          position: "absolute",
          borderRadius: "50%",
        }}
      >
        <ChatBubbleOutlineIcon fontSize="large" />
      </motion.div>

      <motion.div
        ref={thumbUp2Ref}
        style={{
          scale: thumbUp2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "40px",
          width: "40px",
          backgroundColor: "#E50914",
          right: isMobile ? "10%" : "30%",
          top: isMobile ? "15%" : "29%",
          position: "absolute",
          borderRadius: "50%",
        }}
      >
        <ThumbUpAltIcon fontSize="small" />
      </motion.div>

      <motion.div
        ref={thumbUp3Ref}
        style={{
          scale: thumbUp3,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "40px",
          width: "40px",
          backgroundColor: "#1DA1F2",
          left: isMobile ? "30%" : "50%",
          top: isMobile ? "0%" : "5%",
          position: "absolute",
          borderRadius: "50%",
        }}
      >
        <ThumbUpAltIcon fontSize="small" />
      </motion.div>
    </Grid>
  );
};
